import { AuthService } from 'phoenix/services';
import { IExperimental } from 'sharebase/services/trackingService'
import { IStorageAccessor } from 'util/domain';
import SettingsService from 'phoenix/services/settingsService';
import { TrackingService } from 'sharebase/services';

require('./surveyRequest.styles');
const template = require('./surveyRequest.template');

/**
 * Splash screen configuration
 */
export enum UserStats {
    Downloads ='ContentDownloadedByUser', 
    DocumentsCreated = 'DocumentsCreatedByUser', 
    DocumentsViewed = 'DocumentsViewedByUser', 
    DiskUsage = 'LibraryDiskUsage', 
    Logins = 'LoginsByUser',
    SecurityViolations = 'SecurityViolationsByUser', 
    ShareContentDownloaded = 'ShareContentDownloadedByUser',
    Shares = 'SharesByUser',
    DesktopSyncs = 'SyncedByUser',
    UsersCreated = 'UsersCreatedByUser'
}; 

export interface SurveyUserBaseStat{
    stat: UserStats,
    value: number
}

const surveyViewedKey = 'sba-survey-viewed';
const surveyExpDate = 'sba-survey-exp-date';

export type ISurveyRequestService = SurveyRequestService;

/**
 * @ngdoc service
 * @name sharebase.surveyRequest.sbSurveyRequestService
 * 
 * @description
 * Exposes a method for displaying a request to submit a survey screen. The service
 * automatically tracks when the user has viewed the survey screen
 * and determins if it should actually be shown or not.
 */
export class SurveyRequestService implements IExperimental {
    public static readonly $inject = [
        '$q',
        '$mdPanel',
        '$mdMedia',
        'gettextCatalog',
        'localStorageService',
        'settingsService',
        'pxAuthService',
        'sbTrackingService'
    ];

    public readonly experimentID: string = 'feedback-button-text';
    private readonly showScreen: boolean;
    private readonly expirationDate: Date;

    protected get viewed(): boolean {
        let tmp = this.storage.getStoredValue(surveyViewedKey);
        return (tmp && tmp === 'true') || false;
    }

    protected set viewed(val: boolean) {
        this.storage.setStoredValue(surveyViewedKey, val ? 'true' : 'false');
    }

    protected get storedExpiration(): Date {
        let tmp = this.storage.getStoredValue(surveyExpDate);
        if (tmp && !isNaN(Date.parse(tmp))) {
            return new Date(tmp);
        }

        return null;
    }

    protected set storedExpiration(val: Date) {
        if (!val) {
            this.storage.removeStoredValue(surveyExpDate);
        } else {
            this.storage.setStoredValue(surveyExpDate, val.toISOString());
        }
    }

    constructor(
        private readonly $q: ng.IQService,
        private readonly $mdPanel: any,
        private readonly $mdMedia: any,
        private readonly gettextCatalog:any,
        private readonly storage: IStorageAccessor,
        private readonly siteSettings:SettingsService,
        private readonly pxAuthService: AuthService,
        private readonly sbTrackingService:TrackingService
    ) {
        // When should we show the splash screen?
        // 1. An expiration date has been configured, if there is no expiration date,
        //   the service assumes there is no splash screen to show
        // 2. User has not viewed the screen and it has not expired
        // 3. Consumer wishes to display a a different screen than the one the user previously viewed

        // If the survey expiration is ommitted or in an invalid format, just set expiration to
        // undefined. This way the screen will not be shown.
        if (siteSettings.surveyExpiration) {
            let date = siteSettings.surveyExpiration;
            // http://stackoverflow.com/a/1353711/3469752
            if (isNaN(date.getTime())) {
                date = undefined;
            }

            this.expirationDate = date;
        }

        let today = new Date(Date.now());

        let newSurveyRequest = false;
        if (this.expirationDate) {
            newSurveyRequest = !this.storedExpiration || this.storedExpiration.getTime() !== this.expirationDate.getTime();
        }
        this.showScreen = (!this.viewed && today < this.expirationDate) || newSurveyRequest;
        if (newSurveyRequest) {
            // reset viewed stored value first before continuing
            this.storedExpiration = siteSettings.surveyExpiration;
            this.viewed = false;
        }
    }

    /**
     * @ngdoc method
     * @name sbSurveyRequestService#show
     * @methodOf sharebase.surveyRequest.sbSurveyRequestService
     * @kind function
     * 
     * @description
     * Shows the survey request panel or does nothing
     * if the user has already seen the survey panel.
     * 
     * @param {string} message Message to display in the body of the panel
     * 
     * @param {string} url Url to open for the survey
     * 
     * @param {SurveyUserBaseStat[]} [baseStats] Array of stats that must be excedded to show panel
     * 
     * @param {boolean[]} [force=false] Wheither to for the display of panel
     */
    public show(title:string, message:string, url:string, baseStats?: SurveyUserBaseStat[], force?:boolean): ng.IPromise<any> {
        let p: ng.IPromise<any> = this.$q.resolve();

        // Never show if no expiration date is configured (even if opts.force is true)
        // because the service assumes that if there is no expiration date, there is no
        // survey panel to show.
        if (!!this.expirationDate && this.pxAuthService.isAuthenticated && !this.pxAuthService.isShareByLinkUser && (this.showScreen || force)) {

            //loop through base stats
            var userStats = this.pxAuthService.userStatistics;
            let baseStatsMet = baseStats.reduce((accumulator:boolean, currentValue:SurveyUserBaseStat)=>{
                return accumulator ? userStats[currentValue.stat].Count >= currentValue.value : false;
            }, true);

            if(baseStatsMet){ 
                var panelPosition = this.$mdPanel.newPanelPosition()
                    .absolute()
                    .center();
        
                var panelAnimation = this.$mdPanel.newPanelAnimation()
                    .openFrom({
                        top: 0,
                        left: 0
                    })
                    .withAnimation(this.$mdPanel.animation.FADE)
                    .closeTo({
                        top: 0,
                        left: 0
                    });
    
                let acceptButtonText = this.sbTrackingService.getExperimentVariant(this.experimentID) === 0 ? this.gettextCatalog.getString('Take Survey') : this.gettextCatalog.getString('Continue');
							
                p = this.$mdPanel.open({
                    animation: panelAnimation,
                    position: panelPosition,
                    attachTo: document.body,
                    template,
                    controller: SurveyRequestController,
                    controllerAs: 'vm',
                    bindToController: true,
                    locals: {
                        title: title,
                        message: message,
                        url: url,
                        buttonText:acceptButtonText
                    },
                    panelClass:'sb-survey-panel-wrapper',
                    trapFocus: true,
                    focusOnOpen:true,
                    zIndex: 150,
                    clickOutsideToClose: false,
                    escapeToClose: false,
                    hasBackdrop: true
                })
                .then((done) => {                    
                    return done;
                });
            }
        }

        return p;
    }
}

/**
 * @ngdoc directive
 * @name sharebase.surveyRequest.sbSurveyRequest
 * @module sharebase.surveyRequest
 * @restrict E
 * 
 * @description
 * A panel used to request a user complete a survey.
 * This directive should not be used directly, but should only
 * be consumed via @see sharebase.surveyRequest.sbaSurveyRequestService.
 */
class SurveyRequestController {
    public static readonly $inject = [
        '$window',
        'mdPanelRef',
        'localStorageService'
    ];

    public readonly topic: string;
    public readonly url: string;

    constructor(
        private readonly $window,
        private readonly mdPanelRef,
        private readonly storage: IStorageAccessor,
    ) {
    }

    public takeSurvey():void{
        this.$window.open(this.url, '_blank');
        this.markViewed();
        this.mdPanelRef.close(true);
    }

    public close(): void {
        this.markViewed();
        this.mdPanelRef.close(true);
    }
    
    public remindMeLater(): void {
        this.mdPanelRef.close(false);
    }

    private markViewed(): void{
        this.storage.setStoredValue(surveyViewedKey, 'true');
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/components/surveyRequest/surveyRequest.service.ts